import type { Role } from '@koderek/shared';
import type { ReactNode } from 'react';
import { createBrowserRouter } from 'react-router';
import { RequireAuth } from '../features/auth/RequireAuth';
import { RequireRole } from '../features/auth/RequireRole';
import { AccountPage } from '../pages/AccountPage';
import { ActivatePage } from '../pages/ActivatePage';
import { EarlyAccessPage } from '../pages/EarlyAccessPage';
import { EditionPage } from '../pages/EditionPage';
import { FamilyActivitiesPage } from '../pages/FamilyActivitiesPage';
import { FamilyPage } from '../pages/FamilyPage';
import { FamilyQrPage } from '../pages/FamilyQrPage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage';
import { HomePage } from '../pages/HomePage';
import { LoginPage } from '../pages/LoginPage';
import { NewsletterUnsubscribePage } from '../pages/NewsletterUnsubscribePage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { RegisterPage } from '../pages/RegisterPage';
import { RegistrationRedirect, StaffRegistrationRedirect } from '../pages/LegacyRedirects';
import { SetPasswordPage } from '../pages/SetPasswordPage';
import { StartPage } from '../pages/StartPage';
import { Layout } from './Layout';

const authed = (element: ReactNode) => <RequireAuth>{element}</RequireAuth>;

/** Części obsługi i admina ładowane osobno (ADR 07 „Podział kodu”) – rodzina nie pobiera ich kodu. */
function roleRoute(path: string, roles: Role[], load: () => Promise<ReactNode>) {
  return { path, lazy: async () => ({ element: <RequireRole roles={roles}>{await load()}</RequireRole> }) };
}
const staffPages = () => import('../pages/staff/StaffPages');
const checkIn = () => import('../pages/staff/CheckInPages');
const attendance = () => import('../pages/staff/AttendancePages');
const adminList = () => import('../pages/admin/AdminEditionsPage');
const adminEdition = () => import('../pages/admin/AdminEditionPage');
const adminProgram = () => import('../pages/admin/AdminProgramPage');
const adminImport = () => import('../pages/admin/AdminImportPage');
const reports = () => import('../pages/reports/StatsPage');
const adminAccounts = () => import('../pages/admin/AdminAccountsPage');
const adminConsents = () => import('../pages/admin/AdminConsentsPage');
const adminCommunication = () => import('../pages/admin/AdminEditionCommunicationPage');
const adminCampaigns = () => import('../pages/admin/AdminCampaignPages');
const adminMailLog = () => import('../pages/admin/AdminMailLogPage');
const adminFeedback = () => import('../pages/admin/AdminFeedbackPage');
const adminMailFooter = () => import('../pages/admin/AdminMailFooterPage');
const adminSettings = () => import('../pages/admin/AdminSettingsPage');
const adminLanding = () => import('../pages/admin/AdminLandingPage');
const adminPartners = () => import('../pages/admin/AdminPartnersPage');

/**
 * Adresy z maili (aktywacja, ustawienie hasła, wczesny dostęp, edycja, wypis z newslettera) muszą zgadzać się z API:
 * `apps/api/src/auth/auth.service.ts`, `editions/early-access.service.ts`,
 * `communication/newsletter.service.ts`, kod QR `mail/qr.ts` (`/odprawa/:token`).
 */
export const routes = [
  // Tablica na ekrany w szkole – bez nagłówka i menu aplikacji, publiczna (PRD §10 pkt 41).
  { path: 'tablica/:slug', lazy: async () => ({ Component: (await import('../pages/BoardPage')).BoardPage }) },
  {
    element: <Layout />,
    children: [
      // `/` – strona startowa (PRD §10 pkt 37), a bez niej lista edycji; `/edycje` – zawsze lista edycji.
      { index: true, element: <StartPage /> },
      { path: 'edycje', element: <HomePage /> },
      { path: 'edycje/:slug', element: <EditionPage /> },
      { path: 'edycje/:slug/zajecia/:symbol', element: <EditionPage /> },
      // Krok „zgłoszenie” usunięty (decyzja 2026‑09‑28) – stare linki prowadzą dalej.
      { path: 'edycje/:slug/zgloszenie', element: <RegistrationRedirect /> },
      { path: 'wczesny-dostep', element: <EarlyAccessPage /> },
      { path: 'rejestracja', element: <RegisterPage /> },
      { path: 'aktywacja', element: <ActivatePage /> },
      { path: 'logowanie', element: <LoginPage /> },
      { path: 'nie-pamietam-hasla', element: <ForgotPasswordPage /> },
      { path: 'ustaw-haslo', element: <SetPasswordPage /> },
      { path: 'newsletter/wypisz', element: <NewsletterUnsubscribePage /> },
      { path: 'konto', element: authed(<AccountPage />) },
      { path: 'rodzina', element: authed(<FamilyPage />) },
      { path: 'zajecia-rodziny', element: authed(<FamilyActivitiesPage />) },
      { path: 'kod-qr', element: authed(<FamilyQrPage />) },
      // Serwer sprawdza role niezależnie od widoku (NFR-S4).
      roleRoute('obsluga/konta', ['staff', 'admin'], () => staffPages().then((m) => <m.StaffSearchPage />)),
      roleRoute('obsluga/konta/:id', ['staff', 'admin'], () => staffPages().then((m) => <m.StaffAccountPage />)),
      { path: 'obsluga/konta/:id/edycje/:slug', element: <StaffRegistrationRedirect /> },
      roleRoute('obsluga/konta/:id/edycje/:slug/agenda', ['staff', 'admin'], () => staffPages().then((m) => <m.StaffAgendaPage />)),
      // PRD 15 M6: kod QR rodziny prowadzi tu (US-601 AC-2) – bez roli obsługi tylko logowanie.
      roleRoute('odprawa/:token', ['staff', 'admin'], () => checkIn().then((m) => <m.QrTokenPage />)),
      roleRoute('obsluga/odprawa', ['staff', 'admin'], () => checkIn().then((m) => <m.CheckInEditionsPage />)),
      roleRoute('obsluga/odprawa/:slug', ['staff', 'admin'], () => checkIn().then((m) => <m.CheckInHomePage />)),
      roleRoute('obsluga/odprawa/:slug/rodzina/:accountId', ['staff', 'admin'], () => checkIn().then((m) => <m.FamilyCheckInPage />)),
      roleRoute('obsluga/odprawa/:slug/nowa-rodzina', ['staff', 'admin'], () => checkIn().then((m) => <m.OnSiteRegistrationPage />)),
      roleRoute('obsluga/zajecia', ['speaker', 'staff', 'admin'], () => attendance().then((m) => <m.AttendanceEditionsPage />)),
      roleRoute('obsluga/zajecia/:slug', ['speaker', 'staff', 'admin'], () => attendance().then((m) => <m.AttendanceListPage />)),
      roleRoute('obsluga/zajecia/:slug/:activityId', ['speaker', 'staff', 'admin'], () => attendance().then((m) => <m.AttendanceDetailPage />)),
      roleRoute('admin/edycje', ['admin'], () => adminList().then((m) => <m.AdminEditionsPage />)),
      roleRoute('admin/edycje/nowa', ['admin'], () => adminList().then((m) => <m.AdminNewEditionPage />)),
      roleRoute('admin/edycje/:id', ['admin'], () => adminEdition().then((m) => <m.AdminEditionPage />)),
      roleRoute('admin/edycje/:id/program', ['admin'], () => adminProgram().then((m) => <m.AdminProgramPage />)),
      roleRoute('admin/edycje/:id/komunikacja', ['admin'], () => adminCommunication().then((m) => <m.AdminEditionCommunicationPage />)),
      roleRoute('admin/kampanie', ['admin'], () => adminCampaigns().then((m) => <m.AdminCampaignsPage />)),
      roleRoute('admin/kampanie/nowa', ['admin'], () => adminCampaigns().then((m) => <m.AdminCampaignPage />)),
      roleRoute('admin/kampanie/:id', ['admin'], () => adminCampaigns().then((m) => <m.AdminCampaignPage />)),
      roleRoute('admin/opinie', ['admin'], () => adminFeedback().then((m) => <m.AdminFeedbackPage />)),
      roleRoute('admin/poczta', ['admin'], () => adminMailLog().then((m) => <m.AdminMailLogPage />)),
      roleRoute('admin/stopka-maili', ['admin'], () => adminMailFooter().then((m) => <m.AdminMailFooterPage />)),
      roleRoute('admin/ustawienia', ['admin'], () => adminSettings().then((m) => <m.AdminSettingsPage />)),
      roleRoute('admin/strona-startowa', ['admin'], () => adminLanding().then((m) => <m.AdminLandingPage />)),
      roleRoute('admin/partnerzy', ['admin'], () => adminPartners().then((m) => <m.AdminPartnersPage />)),
      roleRoute('statystyki', ['staff', 'admin'], () => reports().then((m) => <m.StatsPage />)),
      roleRoute('statystyki/dzien/:date', ['staff', 'admin'], () => reports().then((m) => <m.StatsPage />)),
      roleRoute('statystyki/:slug', ['staff', 'admin'], () => reports().then((m) => <m.StatsPage />)),
      roleRoute('admin/konta', ['admin'], () => adminAccounts().then((m) => <m.AdminAccountsPage />)),
      roleRoute('admin/zgody', ['admin'], () => adminConsents().then((m) => <m.AdminConsentsPage />)),
      roleRoute('admin/import', ['admin'], () => adminImport().then((m) => <m.AdminImportPage />)),
      roleRoute('admin/typy-zajec', ['admin'], () => adminProgram().then((m) => <m.AdminActivityTypesPage />)),
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
